import React, { useCallback, useEffect, useRef } from 'react';

type SashProps = {
  onResize: (delta: number) => void;
  onResizeEnd?: () => void;
  direction?: 'horizontal' | 'vertical';
  style?: React.CSSProperties;
};

export function Sash({ onResize, onResizeEnd, direction = 'horizontal', style }: SashProps) {
  const isDragging = useRef(false);
  const lastPos = useRef(0);
  const sashRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      lastPos.current = direction === 'horizontal' ? e.clientX : e.clientY;
      if (sashRef.current) {
        sashRef.current.style.background = 'var(--curve-sash-drag-bg, rgba(34,197,94,0.4))';
      }
    },
    [direction]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
      const delta = currentPos - lastPos.current;
      lastPos.current = currentPos;
      onResize(delta);
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      if (sashRef.current) {
        sashRef.current.style.background = '';
      }
      onResizeEnd?.();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [direction, onResize, onResizeEnd]);

  const isHorizontal = direction === 'horizontal';

  return (
    <div
      ref={sashRef}
      onMouseDown={handleMouseDown}
      style={{
        width: isHorizontal ? '4px' : '100%',
        height: isHorizontal ? '100%' : '4px',
        cursor: isHorizontal ? 'col-resize' : 'row-resize',
        flexShrink: 0,
        background: 'transparent',
        transition: 'background 0.15s',
        zIndex: 10,
        ...style,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--curve-sash-hover-bg, rgba(0,0,0,0.12))';
      }}
      onMouseLeave={(e) => {
        if (!isDragging.current) {
          (e.currentTarget as HTMLDivElement).style.background = '';
        }
      }}
    />
  );
}
